import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Star, Loader2, Check, ShoppingCart, X, AlertTriangle,
  ArrowRight as IconArrowRight, Home, Save, Layers, SlidersHorizontal,
  RotateCcw, Search, GraduationCap, MapPin, Zap, LayoutGrid, LayoutList,
  AlertCircle, Heart, Facebook, Instagram, Linkedin, Youtube, X as XIcon
} from "lucide-react";
import { useNavigate, useLocation, Link } from 'react-router-dom';
import CollegeFilter from "./Filter";
import { getColleges, saveDraftSelection } from "../../services/api";
import { useTheme } from '../Home/ThemeContext';
import vidyazonLogo from '../../img/logo3.png';

const MIN_SELECTION = 5;
const MAX_SELECTION = 10;

const formatFee = (fee) => {
  if (!fee) return 'N/A';
  if (typeof fee === 'string' && fee.match(/[^0-9]/) && fee.length < 15) return fee;
  const numericStr = String(fee).replace(/[^0-9.]/g, '');
  const numericFee = parseFloat(numericStr);
  if (isNaN(numericFee) || numericFee === 0) return 'N/A';
  if (numericFee >= 10000000) return `₹${(numericFee / 10000000).toFixed(2)} Cr`;
  if (numericFee >= 100000) return `₹${(numericFee / 100000).toFixed(2)} L`;
  return `₹${numericFee.toLocaleString('en-IN')}`;
};

const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) return imagePath;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001/api';
  const host = backendUrl.replace('/api', '');
  let cleanedPath = imagePath;
  if (cleanedPath.startsWith('/')) cleanedPath = cleanedPath.substring(1);
  if (cleanedPath.startsWith('uploads/')) cleanedPath = cleanedPath.substring(8);
  return `${host}/uploads/${cleanedPath}`;
};

const getCurrentUser = () => {
  try {
    const token = localStorage.getItem('token');
    const userType = localStorage.getItem('userType');
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    return { token, userType, user };
  } catch { return { token: null, userType: null, user: null }; }
};

export default function CollegeSelectionUI() {
  const context = useTheme();
  const isDarkMode = context?.isDarkMode || false;
  const navigate = useNavigate();
  const location = useLocation();
  const listRef = useRef(null);

  const [colleges, setColleges] = useState([]);
  const [selectedColleges, setSelectedColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectionError, setSelectionError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'grid'
  const [filters, setFilters] = useState({
    disciplines: [], countries: [], continents: [], states: [],
    tuitionFee: { min: "", max: "" },
    durations: [], format: [], attendance: [], degreeTypes: [], specialProgrammes: [],
  });
  const [apiFilters, setApiFilters] = useState({});
  const [showCheckout, setShowCheckout] = useState(false);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [showMinSelectionPopup, setShowMinSelectionPopup] = useState(true);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [proceedingToForm, setProceedingToForm] = useState(false);
  const [authError, setAuthError] = useState("");

  const selectionCount = selectedColleges.length;
  const isDoubleFee = selectionCount > 5;

  const getActiveTab = useCallback(() => {
    if (location.pathname.includes('/student/programs')) return 'programs';
    if (location.pathname.includes('/student/scholarships')) return 'scholarships';
    return 'universities';
  }, [location.pathname]);

  const [activeTab, setActiveTab] = useState(getActiveTab());

  // Hide the global root layout footer when this component mounts, restore it when unmounting
  useEffect(() => {
    const globalFooter = document.querySelector('footer');
    if (globalFooter) globalFooter.style.display = 'none';
    return () => {
      if (globalFooter) globalFooter.style.display = '';
    };
  }, []);

  const theme = {
    bg: isDarkMode ? 'bg-slate-900' : 'bg-indigo-50/50',
    sidebar: isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200',
    header: isDarkMode ? 'bg-slate-800/95 border-slate-700' : 'bg-white/95 border-slate-200',
    card: isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200',
    cardHover: isDarkMode ? 'hover:border-indigo-600/50' : 'hover:border-indigo-400',
    textMain: isDarkMode ? 'text-white' : 'text-slate-900',
    textMuted: isDarkMode ? 'text-slate-400' : 'text-slate-500',
    input: isDarkMode ? 'bg-slate-700 border-slate-600 text-white focus:ring-indigo-500' : 'bg-white border-slate-300 text-slate-900 focus:ring-indigo-500',
    popupBg: isDarkMode ? 'bg-slate-800' : 'bg-white',
    footerWrapper: isDarkMode 
      ? 'bg-gradient-to-b from-gray-900 via-black to-black border-gray-800 text-gray-400' 
      : 'bg-gradient-to-b from-white via-gray-50 to-gray-100 border-gray-200 text-gray-600',
    footerGlass: isDarkMode 
      ? 'bg-gray-800/40 border-gray-700/50 backdrop-blur-md shadow-xl shadow-black/20 text-white' 
      : 'bg-white/60 border-white/50 backdrop-blur-md shadow-lg shadow-blue-900/5 text-gray-900',
  };

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchQuery), 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('q');
    if (q) { setSearchQuery(q); setDebouncedSearch(q); }
    setActiveTab(getActiveTab());
  }, [location.search, getActiveTab]);

  useEffect(() => {
    setPage(1);
    loadColleges(1);
  }, [debouncedSearch, apiFilters]);

  useEffect(() => {
    if (page > 1) loadColleges(page);
  }, [page]);

  const loadColleges = async (currentPage = page) => {
    try {
      setLoading(true);
      const res = await getColleges({ page: currentPage, limit: 10, q: debouncedSearch, ...apiFilters });
      const backend = res.data;
      const newColleges = backend.data || [];
      setColleges(prev => currentPage === 1 ? newColleges : [...prev, ...newColleges]);
      setTotalPages(backend.totalPages || 1);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
    const params = {};
    if (newFilters.disciplines?.length) params.courses = newFilters.disciplines.join(',');
    if (newFilters.countries?.length) params.countries = newFilters.countries.join(',');
    if (newFilters.states?.length) params.states = newFilters.states.join(',');
    if (newFilters.continents?.length) params.continents = newFilters.continents.join(',');
    if (newFilters.tuitionFee?.min || newFilters.tuitionFee?.max) {
      params.tuitionFee = `${newFilters.tuitionFee.min || 0},${newFilters.tuitionFee.max || 100000000}`;
    }
    if (newFilters.durations?.length) params.durations = newFilters.durations.join(',');
    if (newFilters.format?.length) params.format = newFilters.format.join(',');
    if (newFilters.attendance?.length) params.attendance = newFilters.attendance.join(',');
    if (newFilters.degreeTypes?.length) params.degreeTypes = newFilters.degreeTypes.join(',');
    if (newFilters.specialProgrammes?.length) params.specialProgrammes = newFilters.specialProgrammes.join(',');
    setApiFilters(params);
    setPage(1);
  }, []);

  const handleApplyFilters = useCallback(() => {
    setMobileFilterOpen(false);
  }, []);

  const clearAllFilters = () => {
    setFilters({ disciplines: [], countries: [], continents: [], states: [], tuitionFee: { min: "", max: "" }, durations: [], format: [], attendance: [], degreeTypes: [], specialProgrammes: [] });
    setApiFilters({});
    setSearchQuery("");
    setDebouncedSearch("");
    setPage(1);
  };

  const getCollegeId = (c) => c._id || c.id;
  const isSelected = (college) => selectedColleges.some(c => getCollegeId(c) === getCollegeId(college));

  const toggleCollegeSelection = (college) => {
    const id = getCollegeId(college);
    if (!id) return;
    setSelectedColleges(prev => {
      const exists = prev.some(c => getCollegeId(c) === id);
      if (exists) { setSelectionError(""); return prev.filter(c => getCollegeId(c) !== id); }
      if (prev.length >= MAX_SELECTION) { setSelectionError(`Limit Reached: Maximum ${MAX_SELECTION} colleges allowed.`); return prev; }
      setSelectionError("");
      return [...prev, college];
    });
  };

  const handleSaveDraft = async () => {
    const { token, userType } = getCurrentUser();
    setAuthError("");
    if (!token) {
      localStorage.setItem('tempDraftSelection', JSON.stringify(selectedColleges));
      navigate('/login?type=student', { state: { returnUrl: location.pathname } });
      return;
    }
    if (userType === 'partner' || userType === 'college') {
      setAuthError(`You are logged in as ${userType}. Please login as a Student to save your selection.`);
      return;
    }
    if (userType !== 'student') {
      setAuthError("Please login as a Student to save your selection.");
      return;
    }
    if (selectedColleges.length === 0) { setSelectionError("Please select at least one college."); return; }

    try {
      setSavingDraft(true);
      setSelectionError("");
      const validIds = selectedColleges.map(c => { const id = c._id || c.id || c.collegeId; return id ? String(id) : null; }).filter(Boolean);
      if (validIds.length === 0) { setSelectionError("Error: Could not find valid IDs."); return; }
      await saveDraftSelection({ collegeIds: validIds });
      setShowSaveSuccess(true);
    } catch (err) {
      const backendMsg = err.response?.data?.message || err.response?.data?.error;
      setSelectionError(backendMsg || "Failed to save draft.");
    } finally {
      setSavingDraft(false);
    }
  };

  const handleProceedToForm = async () => {
    const { token, userType } = getCurrentUser();
    setAuthError("");
    if (!token) { navigate('/login?type=student', { state: { returnUrl: location.pathname } }); return; }
    if (userType === 'partner' || userType === 'college') {
      setAuthError(`You are logged in as ${userType}. Please login as a Student to proceed.`);
      return;
    }
    try {
      setProceedingToForm(true);
      const selectionData = {
        colleges: selectedColleges.map(c => ({ id: getCollegeId(c), name: c.collegeName, logoUrl: c.logoUrl || c.collegeLogo || c.imageUrl })),
        totalSelected: selectedColleges.length,
        isDoubleFee
      };
      localStorage.setItem('pendingCollegeSelection', JSON.stringify(selectionData));
      navigate('/student-form', { state: { selectedColleges: selectionData.colleges, isDoubleFee } });
    } catch (err) {
      console.error(err);
    } finally {
      setProceedingToForm(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'programs') navigate('/student/programs' + location.search);
    else if (tab === 'scholarships') navigate('/student/scholarships' + location.search);
    else navigate('/student/colleges' + location.search);
  };

  const GridCard = ({ college }) => {
    const selected = isSelected(college);
    const logoUrl = getImageUrl(college.logoUrl || college.collegeLogo || college.imageUrl);
    const course = college.courses?.[0] || {};
    const fee = formatFee(course?.Fees || college.feeMin_INR);

    return (
      <div className={`group relative rounded-2xl border transition-all duration-300 hover:shadow-xl hover:shadow-indigo-500/10 overflow-hidden flex flex-col
        ${selected ? 'border-indigo-600 bg-indigo-50/50 dark:bg-indigo-900/10 ring-1 ring-indigo-500/50' : `${theme.card}${theme.cardHover}`}`}>
        {selected && (
          <div className="absolute top-0 right-0 bg-indigo-600 text-white text-[10px] font-bold px-3 py-1 rounded-bl-xl z-10">SELECTED</div>
        )}
        <div className={`p-4 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-100'}`}>
          <div className="w-full h-24 flex items-center justify-center bg-white rounded-xl mb-3 border border-slate-100 p-2">
            {logoUrl ? (
              <img src={logoUrl} alt={college.collegeName} className="max-w-full max-h-full object-contain"
                onError={e => { e.target.onerror = null; e.target.src = "https://via.placeholder.com/80"; }} />
            ) : (
              <GraduationCap className="w-10 h-10 text-indigo-300" />
            )}
          </div>
          <h3 className={`font-bold text-sm text-center line-clamp-2 group-hover:text-indigo-600 transition-colors ${theme.textMain}`}>
            {college.collegeName}
          </h3>
        </div>
        <div className="p-3 flex-1 flex flex-col gap-2">
          <div className={`flex items-center gap-1 text-xs ${theme.textMuted}`}>
            <MapPin className="w-3 h-3 text-indigo-400 flex-shrink-0" />
            <span className="truncate">{college.city}{college.city && college.state ? ', ' : ''}{college.state}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1 text-xs font-bold text-amber-500">
              <Star className="w-3 h-3 fill-current" /> {college.rating || "N/A"}
            </span>
            {fee !== 'N/A' && <span className="text-xs font-bold text-emerald-600">{fee}</span>}
          </div>
          <div className="mt-auto pt-2 flex gap-2">
            <button onClick={() => toggleCollegeSelection(college)}
              className={`flex-1 py-2 rounded-xl text-xs font-bold flex items-center justify-center gap-1 transition-all active:scale-95 text-white
                ${selected ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-gradient-to-r from-indigo-600 to-indigo-700 hover:brightness-110'}`}>
              {selected ? <><Check className="w-3 h-3" /> Added</> : 'Select'}
            </button>
            <button onClick={() => navigate(`/college/${getCollegeId(college)}`)}
              className={`p-2 rounded-xl border text-xs font-bold transition-all ${theme.card} text-indigo-600 hover:bg-indigo-50`}>
              <IconArrowRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const ListCard = ({ college }) => {
    const selected = isSelected(college);
    const logoUrl = getImageUrl(college.logoUrl || college.collegeLogo || college.imageUrl);
    const course = college.courses?.[0] || {};
    const fee = formatFee(course?.Fees || college.feeMin_INR);

    return (
      <div className={`group relative rounded-2xl border transition-all duration-300 hover:shadow-xl hover:shadow-indigo-500/10 overflow-hidden
        ${selected ? 'border-indigo-600 bg-indigo-50/50 dark:bg-indigo-900/10 ring-1 ring-indigo-500/50' : `${theme.card}${theme.cardHover}`}`}>
        {selected && (
          <div className="absolute top-0 right-0 bg-indigo-600 text-white text-[10px] font-bold px-3 py-1 rounded-bl-xl z-10">SELECTED</div>
        )}
        <div className="flex flex-col sm:flex-row p-4 sm:p-6 gap-4 sm:gap-6">
          <div className="flex items-start gap-4 flex-1 min-w-0">
            <div className="flex-shrink-0 w-16 h-16 sm:w-24 sm:h-24 rounded-2xl border bg-white p-2 shadow-sm flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
              {logoUrl ? (
                <img src={logoUrl} alt={college.collegeName} className="max-w-full max-h-full object-contain"
                  onError={e => { e.target.onerror = null; e.target.src = "https://via.placeholder.com/80"; }} />
              ) : (
                <GraduationCap className="w-8 h-8 sm:w-10 sm:h-10 text-indigo-300" />
              )}
            </div>
            <div className="flex-1 min-w-0 pt-1">
              <h3 className={`text-base sm:text-xl font-bold truncate pr-4 mb-1 group-hover:text-indigo-600 transition-colors ${theme.textMain}`}>
                {college.collegeName}
              </h3>
              {(college.country || college.continent) && (
                <p className={`text-xs sm:text-sm mb-1 font-medium ${theme.textMuted}`}>
                  <span className="font-semibold">{college.country || 'Global'}</span>
                  {college.continent && <span className="text-xs opacity-70"> ({college.continent})</span>}
                </p>
              )}
              <div className="flex flex-wrap gap-2 sm:gap-3 text-xs sm:text-sm mb-2">
                <span className={`flex items-center gap-1 ${theme.textMuted}`}>
                  <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-indigo-400" />
                  {college.city}{college.city && college.state ? ', ' : ''}{college.state}
                </span>
                <span className="flex items-center gap-1 font-bold text-amber-500">
                  <Star className="w-3 h-3 sm:w-4 sm:h-4 fill-current" /> {college.rating || "N/A"}
                </span>
              </div>
              {course?.name && (
                <div className={`hidden sm:inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-bold ${isDarkMode ? 'bg-slate-700 text-indigo-300' : 'bg-indigo-50 text-indigo-700'}`}>
                  <Layers className="w-3 h-3" />
                  <span className="truncate max-w-[160px]">{course.name}</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-row sm:flex-col items-center justify-between sm:justify-end gap-3 sm:gap-4 sm:min-w-[130px] sm:border-l sm:border-slate-100 sm:dark:border-slate-800 sm:pl-5">
            {fee !== 'N/A' && (
              <div className={`p-2 sm:p-3 rounded-xl border border-slate-200/50 ${theme.popupBg}`}>
                <span className="text-base sm:text-xl font-extrabold text-emerald-600 block">{fee}</span>
                <p className="text-xs font-medium mt-0.5 text-slate-500">Total Fee</p>
              </div>
            )}
            <div className="flex sm:flex-col gap-2 w-auto sm:w-full">
              <button onClick={() => toggleCollegeSelection(college)}
                className={`py-2 px-3 sm:w-full rounded-xl text-xs sm:text-sm font-bold flex items-center justify-center gap-1.5 transition-all active:scale-95 text-white shadow-lg shadow-indigo-500/30
                  ${selected ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-gradient-to-r from-indigo-600 to-indigo-700 hover:brightness-110'}`}>
                {selected ? <><Check className="w-4 h-4" /> Added</> : 'Select'}
              </button>
              <button onClick={() => navigate(`/college/${getCollegeId(college)}`)}
                className={`py-2 px-3 sm:w-full text-center text-xs font-bold transition-all rounded-xl border ${theme.card} text-indigo-600 hover:bg-indigo-50`}>
                Details
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`min-h-screen mt-16 w-full flex flex-col font-sans ${theme.bg}`}>

      {/* AUTH ERROR MODAL */}
      {authError && (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
          <div className={`${theme.popupBg} rounded-3xl shadow-2xl max-w-sm w-full p-8 border border-red-200`}>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h3 className={`text-xl font-bold text-center mb-2 ${theme.textMain}`}>Access Restricted</h3>
            <p className={`text-sm text-center mb-6 ${theme.textMuted}`}>{authError}</p>
            <div className="space-y-3">
              <button onClick={() => { setAuthError(""); navigate('/student/dashboard'); }}
                className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-indigo-600 to-indigo-700">
                Go to Student Dashboard
              </button>
              <button onClick={() => setAuthError("")}
                className={`w-full py-3 rounded-xl font-bold ${isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100'}`}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SELECTION RULES POPUP */}
      {showMinSelectionPopup && (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className={`${theme.popupBg} rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden border border-indigo-600/20`}>
            <div className="h-28 bg-gradient-to-r from-indigo-600 to-indigo-700 relative p-6 flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-white rounded-full p-1">
                  <img src={vidyazonLogo} alt="Vidyazon Logo" className="h-8 w-auto" />
                </div>
                <span className="text-white text-xl font-extrabold tracking-wider">VIDYAZON</span>
              </div>
            </div>
            <div className="pt-4 px-6 sm:px-8 pb-8">
              <h3 className={`text-2xl font-bold ${theme.textMain} mb-2`}>Selection Strategy</h3>
              <p className={`${theme.textMuted} mb-6 text-sm`}>
                To maximize your admission chances, select between{' '}
                <span className="text-indigo-600 font-bold">{MIN_SELECTION} – {MAX_SELECTION} colleges</span>.
              </p>
              <div className="space-y-3 mb-8">
                <div className={`flex items-center justify-between p-4 rounded-xl border-l-4 border-emerald-500 ${isDarkMode ? 'bg-emerald-500/10' : 'bg-emerald-100'}`}>
                  <div>
                    <p className="font-bold text-emerald-700 dark:text-emerald-400">Standard Fee</p>
                  </div>
                  <span className="font-mono font-bold text-lg text-emerald-600">1 – 5 Colleges</span>
                </div>
                <div className={`flex items-center justify-between p-4 rounded-xl border-l-4 border-amber-500 ${isDarkMode ? 'bg-amber-500/10' : 'bg-amber-100'}`}>
                  <div>
                    <p className="font-bold text-amber-700 dark:text-amber-400">Double Fee</p>
                  </div>
                  <span className="font-mono font-bold text-lg text-amber-600">6 – 10 Colleges</span>
                </div>
              </div>
              <button onClick={() => setShowMinSelectionPopup(false)}
                className="w-full py-4 rounded-xl font-bold text-white shadow-xl shadow-indigo-500/30 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:brightness-110 active:scale-[0.98] transition-all mb-4">
                Start Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SAVE SUCCESS MODAL */}
      {showSaveSuccess && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[110] p-4">
          <div className={`${theme.popupBg} rounded-[2rem] shadow-2xl max-w-sm w-full border border-indigo-300 relative overflow-hidden`}>
            <div className="p-8 flex flex-col items-center text-center relative z-10">
              <div className="w-20 h-20 bg-gradient-to-tr from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center shadow-lg shadow-emerald-500/30 mb-6">
                <Check className="w-10 h-10 text-white stroke-[3]" />
              </div>
              <h3 className={`text-2xl font-bold mb-2 ${theme.textMain}`}>Draft Saved!</h3>
              <p className={`text-sm mb-8 ${theme.textMuted}`}>
                Your selection of <span className="font-bold text-indigo-500">{selectedColleges.length} colleges</span> has been securely saved.
              </p>
              <div className="w-full space-y-3">
                <button onClick={() => navigate('/student/dashboard')}
                  className="w-full py-3.5 rounded-xl font-bold text-white shadow-lg active:scale-95 transition-all bg-gradient-to-r from-indigo-600 to-indigo-700">
                  Go to Dashboard
                </button>
                <button onClick={() => setShowSaveSuccess(false)}
                  className={`w-full py-3.5 rounded-xl font-bold transition-colors ${isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100'}`}>
                  Continue Searching
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-row relative min-h-0">
        {/* SIDEBAR Desktop */}
        <aside className={`hidden lg:block w-72 flex-none border-r sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto custom-scrollbar z-20 ${theme.sidebar}`}>
          <CollegeFilter currentFilters={filters} onFilterChange={handleFilterChange} onClearAll={clearAllFilters} onApplyFilters={handleApplyFilters} />
        </aside>

        {/* MOBILE FILTER DRAWER */}
        {mobileFilterOpen && (
          <div className="fixed inset-0 z-[100] flex justify-end lg:hidden">
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setMobileFilterOpen(false)} />
            <div className={`relative w-[90%] max-w-xs h-full shadow-2xl flex flex-col overflow-hidden ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
              <div className={`flex items-center justify-between p-5 border-b ${isDarkMode ? 'border-slate-800' : 'border-slate-100'}`}>
                <h3 className={`text-lg font-bold ${theme.textMain}`}>Filter Colleges</h3>
                <button onClick={() => setMobileFilterOpen(false)} className={`p-2 rounded-full ${theme.textMuted}`}>
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto custom-scrollbar p-2">
                <CollegeFilter currentFilters={filters} onFilterChange={handleFilterChange} onClearAll={clearAllFilters} onApplyFilters={handleApplyFilters} />
              </div>
            </div>
          </div>
        )}

        <main className="flex-1 flex flex-col min-w-0">
          {/* STICKY HEADER */}
          <div className={`sticky top-16 z-10 backdrop-blur-md border-b shadow-sm ${theme.header}`}>
            <div className="px-4 sm:px-8 py-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <div className="relative flex-1 w-full group">
                <Search className="absolute left-4 top-3.5 w-5 h-5 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
                <input type="text" placeholder="Search universities, cities..." value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`w-full pl-12 pr-4 py-3 rounded-full border outline-none ring-offset-2 focus:ring-2 transition-all font-medium shadow-sm text-sm sm:text-base ${theme.input}`} />
              </div>
              <div className="flex items-center gap-2">
                <div className={`flex p-1.5 rounded-full ${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'} border border-slate-200/50`}>
                  {['universities', 'programs', 'scholarships'].map(tab => (
                    <button key={tab} onClick={() => handleTabChange(tab)}
                      className={`px-3 py-1.5 text-xs sm:text-sm font-bold rounded-full transition-all capitalize
                        ${activeTab === tab ? 'bg-white dark:bg-slate-700 text-indigo-600 shadow-md scale-105' : 'text-slate-500 hover:text-slate-800'}`}>
                      {tab}
                    </button>
                  ))}
                </div>
                <div className={`flex p-1 rounded-xl border ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-slate-100 border-slate-200'}`}>
                  <button onClick={() => setViewMode('list')}
                    className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-white dark:bg-slate-600 shadow text-indigo-600' : 'text-slate-400 hover:text-slate-600'}`}>
                    <LayoutList className="w-4 h-4" />
                  </button>
                  <button onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-white dark:bg-slate-600 shadow text-indigo-600' : 'text-slate-400 hover:text-slate-600'}`}>
                    <LayoutGrid className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Progress Row */}
            <div className="px-4 sm:px-8 pb-3 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 dark:border-slate-800 pt-2">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="relative flex-shrink-0">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-base font-bold border-2 ${selectedColleges.length >= MIN_SELECTION ? 'border-indigo-600 text-indigo-600 bg-indigo-50' : 'border-slate-300 text-slate-400'}`}>
                    {selectedColleges.length}
                  </div>
                </div>
                <div className="flex flex-col min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-bold uppercase tracking-wider ${theme.textMuted}`}>Selection</span>
                    {isDoubleFee
                      ? <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700">Double Fee</span>
                      : <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700">Standard Fee</span>
                    }
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={handleSaveDraft} disabled={savingDraft || selectedColleges.length === 0}
                  className={`hidden sm:flex items-center gap-2 px-4 py-2 rounded-xl text-xs sm:text-sm font-bold border transition-all ${theme.card} ${theme.textMain} disabled:opacity-50`}>
                  {savingDraft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 text-indigo-600" />}
                  {savingDraft ? 'Saving...' : 'Draft'}
                </button>
                <button onClick={() => setShowCheckout(true)} disabled={selectedColleges.length < MIN_SELECTION}
                  className={`flex items-center gap-1.5 px-5 py-2 rounded-xl text-xs sm:text-sm font-bold text-white shadow-lg shadow-indigo-500/30 transition-all
                    ${selectedColleges.length >= MIN_SELECTION ? 'bg-gradient-to-r from-indigo-600 to-indigo-700 hover:brightness-110' : 'bg-slate-300 cursor-not-allowed opacity-70'}`}>
                  <ShoppingCart className="w-4 h-4" /> <span>Checkout</span>
                </button>
              </div>
            </div>
          </div>

          {/* COLLEGE LIST CONTAINER */}
          <div ref={listRef} className="p-4 sm:p-6 lg:p-8 flex-1">
            {loading && page === 1 ? (
              <div className="flex flex-col items-center justify-center py-32 opacity-70">
                <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mb-4" />
                <p className={`font-medium animate-pulse ${theme.textMuted}`}>Curating top universities for you...</p>
              </div>
            ) : colleges.length > 0 ? (
              <div className={viewMode === 'grid'
                ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5'
                : 'grid grid-cols-1 gap-4'
              }>
                {colleges.map(college => viewMode === 'grid'
                  ? <GridCard key={getCollegeId(college)} college={college} />
                  : <ListCard key={getCollegeId(college)} college={college} />
                )}
                {page < totalPages && (
                  <div className={`${viewMode === 'grid' ? 'col-span-full' : ''} py-8 flex justify-center`}>
                    <button onClick={() => setPage(p => p + 1)}
                      className={`px-8 py-3 rounded-full font-bold shadow-lg hover:-translate-y-1 transition-all flex items-center gap-2 group ${theme.card} text-indigo-600`}>
                      {loading ? <Loader2 className="animate-spin" /> : <>Load More <IconArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></>}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-24">
                <h2 className={`text-2xl font-bold mb-3 ${theme.textMain}`}>No Matches Found</h2>
                <button onClick={clearAllFilters}
                  className="px-8 py-3 rounded-full font-bold shadow-lg text-white bg-gradient-to-r from-indigo-600 to-indigo-700">
                  <RotateCcw className="w-4 h-4 inline mr-2" /> Reset Filters
                </button>
              </div>
            )}
          </div>

          {/* VERY COMPACT & CLEAN CUSTOM FOOTER FOR THIS PAGE ONLY */}
          <footer className={`relative w-full border-t py-6 px-6 transition-colors duration-500 ${theme.footerWrapper}`}>
            <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="font-black tracking-wide text-base text-white">
                  VIDYA<span className="text-blue-600">ZON</span>
                </span>
                <span className="text-xs opacity-60">| AI precision meets expert mentorship.</span>
              </div>
              <div className="flex items-center gap-6 text-xs font-medium">
                <Link to="/about" className="hover:text-blue-500 transition-colors">About</Link>
                <Link to="/contact" className="hover:text-blue-500 transition-colors">Contact</Link>
                <Link to="/privacy-policy" className="hover:text-blue-500 transition-colors">Privacy</Link>
                <Link to="/t&c" className="hover:text-blue-500 transition-colors">Terms</Link>
              </div>
              <div className="flex items-center gap-3">
                {[
                  { Icon: Youtube, href: "https://www.youtube.com/@vidyazon" },
                  { Icon: Instagram, href: "https://www.instagram.com/vidyazon_/" },
                  { Icon: Linkedin, href: "https://www.linkedin.com/in/vidyazon-c-o-aespleduzone-a8857539b" },
                  { Icon: Facebook, href: "https://www.facebook.com/vidyazon" },
                  { Icon: XIcon, href: "https://x.com/AesplEduzone" }
                ].map(({ Icon, href }, idx) => (
                  <a key={idx} href={href} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-full hover:bg-blue-600 hover:text-white transition-all">
                    <Icon size={13} />
                  </a>
                ))}
              </div>
            </div>
            <div className="max-w-5xl mx-auto mt-4 pt-4 border-t border-gray-800/40 flex flex-col sm:flex-row justify-between items-center text-[10px] tracking-wider font-medium opacity-60">
              <p>&copy; {new Date().getFullYear()} Vidyazon. All rights reserved.</p>
              <p className="flex items-center gap-1 mt-1 sm:mt-0">
                Designed with <Heart size={10} className="text-red-500 fill-red-500" /> by Shivansh
              </p>
            </div>
          </footer>

          {/* MOBILE FILTER BUTTON */}
          <button onClick={() => setMobileFilterOpen(true)}
            className="lg:hidden fixed bottom-6 right-6 z-50 text-white px-5 py-3 rounded-full shadow-2xl flex items-center gap-2 font-bold bg-gradient-to-r from-indigo-600 to-indigo-700">
            <SlidersHorizontal className="w-5 h-5" /> Filters
          </button>
        </main>
      </div>

      {/* CHECKOUT MODAL */}
      {showCheckout && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-md flex items-center justify-center z-[70] p-4">
          <div className={`${theme.popupBg} rounded-[2rem] shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-indigo-500/10 p-6`}>
            <div className="flex justify-between items-center mb-6">
              <h2 className={`text-xl font-bold ${theme.textMain}`}>Confirm Selection</h2>
              <button onClick={() => setShowCheckout(false)}><X className="w-6 h-6 text-slate-500" /></button>
            </div>
            <div className="space-y-3 mb-6 max-h-60 overflow-y-auto">
              {selectedColleges.map((c, index) => (
                <div key={getCollegeId(c)} className={`flex justify-between items-center p-3 rounded-xl border ${theme.card}`}>
                  <span className={`font-bold text-sm ${theme.textMain}`}>{c.collegeName}</span>
                  <Check className="w-4 h-4 text-emerald-500" />
                </div>
              ))}
            </div>
            <button onClick={handleProceedToForm} disabled={proceedingToForm}
              className="w-full py-4 rounded-xl text-white font-bold text-base shadow-xl bg-gradient-to-r from-indigo-600 to-indigo-700">
              {proceedingToForm ? <Loader2 className="animate-spin" /> : 'Proceed to Application'}
            </button>
          </div>
        </div>
      )}

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background-color: rgba(99, 102, 241, 0.2); border-radius: 20px; }
      `}</style>
    </div>
  );
}